/*
 * Fragment navigation for on-page sections.
 *
 * Covers: eased smooth scroll, target focus transition, scroll spy with a
 * sliding indicator, scroll progress, sticky-header offset, History-based
 * fragment updates, and IntersectionObserver section reveals.
 *
 * Progressive enhancement: every visual state this script introduces is also
 * introduced by this script, so a parse failure leaves the page fully readable
 * and native fragment jumps still work.
 */
(() => {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const anchorSelector = '.skafe-anchor-target[id]';

  const prefersReducedMotion = () => reduceMotion.matches;

  /* ---------------------------------------------------------------- helpers */

  // Spring-like settle: fast departure, long gentle arrival. Matches the
  // cubic-bezier(0.22, 1, 0.36, 1) used for the CSS transitions.
  const easeOutQuint = (t) => 1 - Math.pow(1 - t, 5);

  const scrollOffset = () => {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--skafe-anchor-offset');
    const parsed = parseFloat(raw);
    if (!Number.isFinite(parsed)) return 0;
    // The custom property is authored in rem.
    return raw.includes('rem') ? parsed * parseFloat(getComputedStyle(document.documentElement).fontSize) : parsed;
  };

  const targetTop = (el) => {
    const max = document.documentElement.scrollHeight - window.innerHeight;
    const top = window.scrollY + el.getBoundingClientRect().top - scrollOffset();
    return Math.max(0, Math.min(top, max));
  };

  /* --------------------------------------- 5. sticky header offset tracking */

  const stickyHeader = document.querySelector('sticky-header, .section-header');

  const syncAnchorOffset = () => {
    if (!stickyHeader) return;
    const height = stickyHeader.getBoundingClientRect().height;
    if (!height) return;
    // Header height plus a little breathing room above the section edge.
    document.documentElement.style.setProperty('--skafe-anchor-offset', `${height + 16}px`);
  };

  syncAnchorOffset();
  if (stickyHeader && 'ResizeObserver' in window) {
    new ResizeObserver(syncAnchorOffset).observe(stickyHeader);
  }
  window.addEventListener('resize', syncAnchorOffset, { passive: true });

  /* ------------------------------------------- 1 + 2. eased scroll to target */

  let activeScroll = null;

  const cancelScroll = () => {
    if (activeScroll) cancelAnimationFrame(activeScroll);
    activeScroll = null;
  };

  const flashFocus = (el) => {
    el.classList.remove('is-anchor-focus');
    // Force a reflow so the animation restarts when the same target is
    // navigated to twice in a row.
    void el.offsetWidth;
    el.classList.add('is-anchor-focus');
    el.addEventListener('animationend', () => el.classList.remove('is-anchor-focus'), { once: true });
  };

  // Move keyboard focus to the target without letting the browser perform its
  // own instant scroll — otherwise the animation is cancelled on arrival.
  const focusTarget = (el) => {
    const hadTabIndex = el.hasAttribute('tabindex');
    if (!hadTabIndex) el.setAttribute('tabindex', '-1');
    el.focus({ preventScroll: true });
    if (!hadTabIndex) {
      el.addEventListener('blur', () => el.removeAttribute('tabindex'), { once: true });
    }
  };

  const settle = (el) => {
    document.body.classList.remove('skafe-anchor-navigating');
    focusTarget(el);
    if (!prefersReducedMotion()) flashFocus(el);
  };

  const scrollToTarget = (el) => {
    cancelScroll();

    if (prefersReducedMotion()) {
      window.scrollTo(0, targetTop(el));
      settle(el);
      return;
    }

    const from = window.scrollY;
    const to = targetTop(el);
    const distance = to - from;

    if (Math.abs(distance) < 2) {
      settle(el);
      return;
    }

    // Longer journeys get more time, but never enough to feel sluggish.
    const duration = Math.min(900, Math.max(420, Math.abs(distance) * 0.55));
    const start = performance.now();

    document.body.classList.add('skafe-anchor-navigating');
    // A recalculated end point keeps us honest if lazy media resizes the page
    // mid-flight.
    let end = to;

    const step = (now) => {
      const progress = Math.min(1, (now - start) / duration);
      if (progress < 0.5) end = targetTop(el);
      window.scrollTo(0, from + (end - from) * easeOutQuint(progress));
      if (progress < 1) {
        activeScroll = requestAnimationFrame(step);
        return;
      }
      activeScroll = null;
      settle(el);
    };

    activeScroll = requestAnimationFrame(step);
  };

  // A user-initiated wheel/touch gesture always wins over an in-flight scroll.
  ['wheel', 'touchstart', 'keydown'].forEach((evt) =>
    window.addEventListener(
      evt,
      () => {
        if (activeScroll) {
          cancelScroll();
          document.body.classList.remove('skafe-anchor-navigating');
        }
      },
      { passive: true }
    )
  );

  /* ------------------------------------------------ 6. URL fragment handling */

  const targetFor = (hash) => {
    if (!hash || hash === '#') return null;
    let id;
    try {
      id = decodeURIComponent(hash.slice(1));
    } catch {
      id = hash.slice(1);
    }
    if (!id) return null;
    const el = document.getElementById(id);
    return el && el.matches(anchorSelector) ? el : null;
  };

  // Same-document fragment link? Compare everything but the hash.
  const isSamePageLink = (link) => {
    if (!link.hash) return false;
    const here = window.location;
    return link.pathname === here.pathname && link.search === here.search && link.host === here.host;
  };

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const link = event.target.closest('a[href]');
    if (!link || link.target === '_blank' || !isSamePageLink(link)) return;

    const target = targetFor(link.hash);
    if (!target) return;

    event.preventDefault();
    // 6.1 — keep the fragment in the URL without reloading the document.
    if (window.location.hash !== link.hash) {
      history.pushState({ skafeAnchor: link.hash }, '', link.hash);
    }
    scrollToTarget(target);
  });

  // 6.3 — back/forward through fragments animates too.
  window.addEventListener('popstate', () => {
    const target = targetFor(window.location.hash);
    if (target) scrollToTarget(target);
  });

  // 6.2 — a page loaded with a fragment. The browser has already jumped, so
  // rewind to the top and play the transition once layout has settled.
  const openingHash = window.location.hash;
  if (openingHash) {
    const runOpening = () => {
      const target = targetFor(openingHash);
      if (!target) return;
      if (!prefersReducedMotion()) window.scrollTo(0, 0);
      requestAnimationFrame(() => scrollToTarget(target));
    };
    if (document.readyState === 'complete') runOpening();
    else window.addEventListener('load', runOpening, { once: true });
  }

  /* ------------------------------------------------ 4. scroll progress bar */

  const progress = document.createElement('div');
  progress.className = 'skafe-scroll-progress';
  progress.setAttribute('aria-hidden', 'true');
  const progressBar = document.createElement('div');
  progressBar.className = 'skafe-scroll-progress__bar';
  progress.appendChild(progressBar);
  document.body.appendChild(progress);

  // Where `animation-timeline: scroll()` is supported the CSS drives the bar and
  // this stays off the scroll path entirely.
  const nativeProgress = getComputedStyle(progress).getPropertyValue('--skafe-progress-native').trim() === '1';

  /* -------------------------------------------------- 3. scroll spy + pill */

  const sections = Array.from(document.querySelectorAll(anchorSelector));

  // Prefer an explicitly marked nav; otherwise adopt the header menu when the
  // merchant has pointed menu items at on-page fragments.
  const findNav = () => {
    const explicit = document.querySelector('[data-skafe-section-nav]');
    if (explicit) return explicit;
    const header = document.querySelector('.header__inline-menu');
    if (!header) return null;
    const hasFragmentLinks = Array.from(header.querySelectorAll('a[href]')).some(
      (a) => isSamePageLink(a) && targetFor(a.hash)
    );
    if (!hasFragmentLinks) return null;
    header.setAttribute('data-skafe-section-nav', '');
    return header;
  };

  const nav = findNav();
  let navLinks = [];
  let indicator = null;

  if (nav) {
    navLinks = Array.from(nav.querySelectorAll('a[href]')).filter((a) => isSamePageLink(a) && targetFor(a.hash));
    if (navLinks.length) {
      indicator = document.createElement('span');
      indicator.className = 'skafe-nav-indicator';
      indicator.setAttribute('aria-hidden', 'true');
      nav.appendChild(indicator);
    }
  }

  const moveIndicator = (link) => {
    if (!indicator) return;
    if (!link) {
      indicator.classList.remove('is-visible');
      return;
    }
    const navBox = nav.getBoundingClientRect();
    const box = link.getBoundingClientRect();
    indicator.style.width = `${box.width}px`;
    indicator.style.height = `${box.height}px`;
    indicator.style.transform = `translate3d(${box.left - navBox.left}px, ${box.top - navBox.top}px, 0)`;
    indicator.classList.add('is-visible');
  };

  let currentId = null;

  const setActive = (id) => {
    if (id === currentId) return;
    currentId = id;
    let active = null;
    navLinks.forEach((link) => {
      const match = targetFor(link.hash);
      const isActive = Boolean(match) && match.id === id;
      if (isActive) {
        link.setAttribute('aria-current', 'true');
        active = link;
      } else {
        link.removeAttribute('aria-current');
      }
    });
    moveIndicator(active);
  };

  window.addEventListener('resize', () => moveIndicator(navLinks.find((l) => l.getAttribute('aria-current'))), {
    passive: true,
  });

  /* ----------------------------------------------- 7. reveals + spy driver */

  if ('IntersectionObserver' in window) {
    // Dawn's own reveal system marks elements with `.scroll-trigger` when the
    // theme setting is on. Never animate the same content twice.
    const revealable = sections.filter((el) => !el.querySelector('.scroll-trigger') && !el.closest('.scroll-trigger'));

    if (!prefersReducedMotion()) {
      revealable.forEach((el) => el.setAttribute('data-skafe-reveal', 'pending'));

      const revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.setAttribute('data-skafe-reveal', 'revealed');
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -12% 0px', threshold: 0.05 }
      );

      revealable.forEach((el) => revealObserver.observe(el));

      // Anything already on screen at load reveals immediately, so the first
      // viewport never animates in under the visitor.
      requestAnimationFrame(() => {
        revealable.forEach((el) => {
          const box = el.getBoundingClientRect();
          if (box.top < window.innerHeight && box.bottom > 0) {
            el.setAttribute('data-skafe-reveal', 'revealed');
            revealObserver.unobserve(el);
          }
        });
      });
    }

    // Scroll spy: the section occupying the band just below the sticky header
    // wins.
    if (navLinks.length) {
      const spy = new IntersectionObserver(
        () => {
          const line = scrollOffset() + 1;
          let best = null;
          sections.forEach((el) => {
            const box = el.getBoundingClientRect();
            if (box.top <= line && box.bottom > line) best = el;
          });
          if (!best) {
            const upcoming = sections.find((el) => el.getBoundingClientRect().top > line);
            best = upcoming || sections[sections.length - 1];
          }
          if (best) setActive(best.id);
        },
        { threshold: [0, 0.25, 0.5, 0.75, 1] }
      );
      sections.forEach((el) => spy.observe(el));
    }
  }

  /* ------------------------------------------------------ scroll listener */

  // One passive listener drives the spy line and (only where CSS cannot) the
  // progress bar. rAF-throttled so it never runs more than once per frame.
  let ticking = false;

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      ticking = false;

      if (!nativeProgress) {
        const max = document.documentElement.scrollHeight - window.innerHeight;
        const ratio = max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0;
        progressBar.style.transform = `scaleX(${ratio})`;
      }

      if (!navLinks.length) return;
      const line = scrollOffset() + 1;
      let best = null;
      sections.forEach((el) => {
        const box = el.getBoundingClientRect();
        if (box.top <= line && box.bottom > line) best = el;
      });
      if (best) setActive(best.id);
    });
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
})();
